import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { ICustomerSettings } from '@/core/lib/businessLogicHelper';
import BusinessLogicHelper from '@/core/lib/businessLogicHelper';
import { getMarketPageFromPresetAndDate } from '@/core/oddsApi/helpers';
import type { EnumSportType } from '@/core/oddsApi/oddsApiType';
import {
    EnumEventDateFilterType,
    EnumPresetFilterType,
    EnumTimeZoneType,
    useSportMarketInfoQuery,
} from '@/core/oddsApi/oddsApiType';
import { SportType } from '@/interface/enum';
import { SportInfo } from '@/models/SportInfo';
import { useOddsApiStore } from '@/store/oddsApiStore';

interface ICount{
    total: number;
    simulation: number;
    filterEFootball: number;
}

export function useSportMarketInfo(isEnabled = ref(true)) {
    const variables = computed(() => ({
        query: {
            timeZone: EnumTimeZoneType.UTC__4,
        },
    }));

    const { result, loaded, loading, refetch } = useSportMarketInfoQuery(
        variables,
        computed(() => ({
            pollInterval: 15000,
            enabled: isEnabled.value,
        })),
    );

    const { customerSettings } = storeToRefs(useOddsApiStore());

    const filteredSportEvents = computed(() => {
        if (!result.value) return [];

        return result.value.map(({
            sport,
            queryFilter,
            liveEventCount,
            nonLiveEventCount,
            liveSimulationEventCount,
            nonLiveSimulationEventCount,
            liveFilterEFootballEventCount,
            nonLiveFilterEFootballEventCount,
        }) => {
            const { filteredLiveCount, filteredNonLiveCount } = filterSportEventCount(
                {
                    total: liveEventCount,
                    simulation: liveSimulationEventCount,
                    filterEFootball: liveFilterEFootballEventCount,
                },
                {
                    total: nonLiveEventCount,
                    simulation: nonLiveSimulationEventCount,
                    filterEFootball: nonLiveFilterEFootballEventCount,
                },
                sport,
                customerSettings.value,
            );

            return {
                sport,
                queryFilter,
                liveEventCount: filteredLiveCount,
                nonLiveEventCount: filteredNonLiveCount,
            };
        });
    });

    const sportList = computed(() => {
        if (!filteredSportEvents.value) return [];

        const list = new Map<SportType, SportInfo>();

        filteredSportEvents.value.forEach(({ sport, queryFilter, liveEventCount, nonLiveEventCount }) => {
            const sportType = SportType.fromOddsApi(sport);
            if (!sportType) return;

            if (!list.has(sportType)) {
                list.set(sportType, new SportInfo({ sportType }));
            }

            const currentSportInfo = list.get(sportType)!;
            if (queryFilter.date === EnumEventDateFilterType.All) {
                switch (queryFilter.presetFilter) {
                    case EnumPresetFilterType.Live:
                        currentSportInfo.liveEventCount = liveEventCount;
                        break;
                    case EnumPresetFilterType.NonLive:
                        currentSportInfo.nonLiveEventCount = nonLiveEventCount;
                        break;
                    case EnumPresetFilterType.Outright:
                        currentSportInfo.outrightLiveEventCount = liveEventCount;
                        currentSportInfo.outrightNonLiveEventCount = nonLiveEventCount;
                        break;
                    default:
                }
            } else if (queryFilter.date === EnumEventDateFilterType.TodayExpanded) {
                currentSportInfo.todayExpandedCount = nonLiveEventCount;
            }
        });

        return [...list.values()];
    });

    const mixParlaySportList = computed(() => {
        if (!filteredSportEvents.value) return [];

        return filteredSportEvents.value
            .filter(x => x.queryFilter.presetFilter === EnumPresetFilterType.MixParlay)
            .filter(x => x.queryFilter.date === EnumEventDateFilterType.All)
            .map(({ sport, liveEventCount, nonLiveEventCount }) => {
                const sportType = SportType.fromOddsApi(sport);
                return new SportInfo({
                    sportType,
                    liveEventCount,
                    nonLiveEventCount,
                });
            });
    });

    const sportMarketData = computed(() => {
        if (!filteredSportEvents.value) return [];

        return filteredSportEvents.value.map(({ sport, queryFilter, liveEventCount, nonLiveEventCount }) => (
            {
                sportType: SportType.fromOddsApi(sport),
                marketPageInfo: getMarketPageFromPresetAndDate(queryFilter.presetFilter, queryFilter.date),
                liveEventCount,
                nonLiveEventCount,
            }));
    });

    return {
        loaded,
        loading,
        refetch,
        sportList,
        mixParlaySportList,
        sportMarketData,
    };
}

function filterSportEventCount(
    liveCount: ICount,
    nonLiveCount: ICount,
    sportType: EnumSportType,
    settings: ICustomerSettings,
) {
    const filteredLiveCount = BusinessLogicHelper.filterEventCount(
        {
            eventCount: liveCount.total,
            simulationEventCount: liveCount.simulation,
            filterEFootballEventCount: liveCount.filterEFootball,
        },
        settings,
        sportType,
    );

    const filteredNonLiveCount = BusinessLogicHelper.filterEventCount(
        {
            eventCount: nonLiveCount.total,
            simulationEventCount: nonLiveCount.simulation,
            filterEFootballEventCount: nonLiveCount.filterEFootball,
        },
        settings,
        sportType,
    );

    return {
        filteredLiveCount: Math.max(0, filteredLiveCount),
        filteredNonLiveCount: Math.max(0, filteredNonLiveCount),
    };
}
